import {observer} from "mobx-react";
import React from "react";
import {FormProps} from "../../core/form/formUtils";
import {Validate} from "../../core/form/validate";
import {MdCheckbox, MdTextField} from "../../core/components/fields/fields";
import {ClosedFormBlock, ClosedFormBlockProps, ClosedFormField} from "../../core/form/closedForm";
import {DefinitionType} from "../../core/form/form";
import {createForm} from "../../core/form/createForm";
import {EntityNumberGen, EntityNumberGenGroup} from "../domain/entityNumberGenDomain";
import {IsRequired, Min, HasLength, NumberFormat, RuleResult, valid, invalid} from 'core/form/rules';
import NumberField from "../../core/components/fields/numberField";
import StyledDateInterval from "../../core/components/fields/styledDateInterval";
import moment from "moment";
import entityNumberGenService from "../service/entityNumberGenService";
import {orange500} from "material-ui/styles/colors";
import {LovItem} from "../../core/metamodel/mdEntity";

function PrefixConstantExists({value}): any {
  if (!value) return invalid("Prefix constant was not specified");
  return entityNumberGenService.checkIfEntityNumberGenPrefixExist(value)
    .then((exists) => (exists) ? invalid(`Number gen with prefix ${value} already exists`) : valid());
}

interface EntityNumberGenFormState {
  sameGenNumberPossibility: boolean;
}

@observer
class EntityNumberGenForm extends React.Component<FormProps<EntityNumberGen, EntityNumberGenGroup>, EntityNumberGenFormState> {

  state = {sameGenNumberPossibility: false};

  componentDidMount() {
    let {dataObject} = this.props;

    if (!dataObject.entityNumberGenId && dataObject.prefixConstant && dataObject.padding)
      this.checkSameGenNumberPossibility();
  }

  ValidateDateInterval = ({value}): RuleResult & any => {
    let {dataObject, form} = this.props;

    if (!dataObject.validFrom || !dataObject.validTo)
      return valid();

    const validFrom = moment(dataObject.validFrom);
    const validTo = moment(dataObject.validTo);
    if (validFrom.isAfter(validTo))
      return invalid('Valid from has to be before valid to date');

    if (validTo.diff(validFrom, 'days') < 1)
      return invalid('There has to be at least one day between valid from and valid to dates');

    let validFromField = form.getField("validFrom")
    validFromField.valid = true;
    validFromField.errorText = null;
    let validToField = form.getField("validTo");
    validToField.valid = true;
    validToField.errorText = null;
    return valid();
  }

  checkSameGenNumberPossibility = (): void => {
    let {dataObject} = this.props;

    if (dataObject.prefixConstant && dataObject.padding)
      entityNumberGenService.checkSameGenNumberPossibility(dataObject.prefixConstant, dataObject.padding)
        .then(exist => {
          this.setState({
            sameGenNumberPossibility: exist,
          })
        });
  };

  render() {

    let {dataObject, rootObject, entities, entity} = this.props;
    let fields = dataObject.getFormFields(EntityNumberGenFormDefinition.formName);
    let {padding, prefixConstant, validFrom, validTo, active} = fields;

    const exist: boolean = !!dataObject.entityNumberGenId;

    const openContent: JSX.Element = (
      <div>
        <div className="row">
          {(this.state.sameGenNumberPossibility) ? (<div className="fx col-xs-12">
            <h5 style={{color: orange500}}>This configuration can generate same numbers as existing number generation</h5>
          </div>) : null}
        </div>
        <div className="row">
          <div className="fx col-xs-12 col-sm-3">
            <MdTextField {...prefixConstant.props} disabled={exist} />
            <Validate field={prefixConstant} value={prefixConstant.props.value}
                      onSuccess={this.checkSameGenNumberPossibility}>
              {(exist) ? null : (<PrefixConstantExists/>)}
            </Validate>
          </div>
          <div className="fx col-xs-12 col-sm-3">
            <NumberField {...padding.props} disabled={exist}/>
            <Validate field={padding} value={padding.props.value} onSuccess={this.checkSameGenNumberPossibility}>
              <NumberFormat precision={1} scale={0}/>
              <Min min={1}/>
            </Validate>
          </div>
          <div className="col-xs-12 col-sm-3">
            <StyledDateInterval {...validFrom.props}
                                errorText={validFrom.errorText ? validFrom.errorText : validTo.errorText}
                                valueFrom={validFrom.props.value} valueTo={validTo.props.value}
                                onChangeFrom={validFrom.props.onChange} onChangeTo={validTo.props.onChange}/>
            <Validate field={validFrom} value={validFrom.props.value}>
              <this.ValidateDateInterval/>
            </Validate>
            <Validate field={validTo} value={validTo.props.value}>
              <this.ValidateDateInterval/>
            </Validate>
          </div>
          <div className="fx col-xs-12 col-sm-3">
            <MdCheckbox {...active.props} style={{alignSelf: 'center'}}/>
            <Validate field={active} value={active.props.value}/>
          </div>
        </div>
      </div>
    );

    return openContent;
  }
}


const ClosedContent = ({dataObject, entity, entities, fields, disabled}: ClosedFormBlockProps<EntityNumberGen>) => (
  <div>
    <ClosedFormBlock {...{dataObject, entity, entities, fields}} >
      <ClosedFormField label={fields.prefixConstant.textLabel} value={dataObject.prefixConstant}/>
      <ClosedFormField label={fields.padding.textLabel} value={dataObject.padding}/>
      <ClosedFormField label={fields.validFrom.textLabel} value={dataObject.validFrom}/>
      <ClosedFormField label={fields.validTo.textLabel} value={dataObject.validTo}/>
    </ClosedFormBlock>
  </div>
);


export const EntityNumberGenFormDefinition: DefinitionType = {
  formName: 'EntityNumberGenForm',
  fields: [{
    name: 'prefixConstant',
    validators: ['IsRequired', {validation: 'HasLength', parameters: {max: 10}}]
  }, {
    name: 'padding',
    validators: ['IsRequired']
  }, {
    name: 'validFrom',
    validators: ['IsRequired']
  }, {
    name: 'validTo'
  }, {
    name: 'active'
  }]
};

export default createForm(EntityNumberGenFormDefinition, ClosedContent)(EntityNumberGenForm);



// WEBPACK FOOTER //
// ./src/invoice/component/EntityNumberGenForm.tsx